import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updateClinicalData } from '@/store/slices/formSlice'
import { FormSection } from '@/types/FormSectionInterface'
import { ClinicalData } from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

interface ClinicalInformationProps {
  initialData?: Record<string, unknown>
  onDataChange?: (data: ClinicalData) => void
  onComplete?: (isComplete: boolean) => void
}

const referenceData = [
  {
    id: 1,
    fhir: 'Encounter.class, Encounter.reason, Encounter.period.start',
    hl7: 'ADT A01/A06, PV1',
    location: 'Encounter / Hospital Course',
  },
  {
    id: 2,
    fhir: 'Observation (birth-details; LOINC 9270-0, 9272-6)',
    hl7: 'OBX (delivery feed)',
    location: 'Labor & Delivery',
  },
  {
    id: 3,
    fhir: 'Observation (category =symptom; SNOMED codes)',
    hl7: 'OBX (nursing notes / flowsheet)',
    location: 'History of Present Illness',
  },
  { id: 4, fhir: 'Condition (modifier =intractable)', hl7: 'DG1 / OBX (problem list)', location: 'Assessment & Plan' },
  {
    id: 5,
    fhir: 'DiagnosticReport (Imaging→EEG), ServiceRequest (procedure)',
    hl7: 'ORU_R30 (EEG), OBR',
    location: 'Results - Diagnostics / Plan of Care',
  },
  {
    id: 6,
    fhir: 'DiagnosticReport (ImagingReport) → ImagingStudy',
    hl7: 'ORU_R30 (radiology)',
    location: 'Results - Imaging',
  },
  { id: 7, fhir: 'Observation (US Core VitalSigns)', hl7: 'OBX (vitals panel)', location: 'Vital Signs' },
  { id: 8, fhir: 'Observation (LOINC 8335-2, 8302-2, 8287-5)', hl7: 'OBX', location: 'Growth / Anthropometrics' },
  { id: 9, fhir: 'Observation (category =exam)', hl7: 'OBX + NTE', location: 'Physical Exam' },
  { id: 10, fhir: 'Observation (LOINC 2524-7)', hl7: 'OBX (chem panel)', location: 'Results - Labs' },
  { id: 11, fhir: 'ServiceRequest (Genomics Order), Task (lab workflow)', hl7: 'OML_O21 (LOI)', location: 'Orders' },
  { id: 12, fhir: 'MedicationRequest / MedicationAdministration', hl7: 'RDE / RXA', location: 'Medications' },
  { id: 13, fhir: 'ServiceRequest with occurrenceTiming', hl7: 'OBR (RAD)', location: 'Plan of Care' },
  { id: 14, fhir: 'NutritionOrder', hl7: 'OBR "DIET", ODS', location: 'Nutrition' },
  { id: 15, fhir: 'ServiceRequest (consult), CarePlan', hl7: 'PV1-17 (consulting MD)', location: 'Care Team / Plan' },
  { id: 16, fhir: 'Task (FHIR workflow)', hl7: '—', location: 'Care Plan / Tasks' },
]

const ClinicalInformation = forwardRef<FormSection, ClinicalInformationProps>(
  ({ initialData, onDataChange, onComplete }, ref) => {
    const dispatch = useAppDispatch()
    const { formData } = useAppSelector((state) => state.form)

    // Use Redux state as the source of truth, fallback to props for initial data
    const clinicalData = useMemo(() => {
      // If Redux state exists, use it
      if (formData.clinical) {
        return formData.clinical
      }

      // If no Redux state but we have initial data, use that
      if (initialData && Object.keys(initialData).length > 0) {
        return {
          clinicalNote: String(initialData?.clinicalNote || ''),
          optOutClinicianNotes: Boolean(initialData?.optOutClinicianNotes || false),
        }
      }

      // Fallback to default empty values
      return {
        clinicalNote: '',
        optOutClinicianNotes: false,
      }
    }, [formData.clinical, initialData])

    const [hoveredReference, setHoveredReference] = useState<number | null>(null)
    const [clickedReference, setClickedReference] = useState<number | null>(null)
    const [isTableExpanded, setIsTableExpanded] = useState(false)
    const [isClinicalNotesExpanded, setIsClinicalNotesExpanded] = useState(true)

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const highlightedRef = useRef<HTMLDivElement>(null)

    // Only use clicked reference for switching clinical notes display
    const activeReference = clickedReference

    // Implement the FormSection interface
    useImperativeHandle(ref, () => ({
      isSectionComplete: () => {
        return FormCompletionChecker.isClinicalComplete(clinicalData)
      },
      renderSection: () => {
        // Return the main component render to avoid duplication
        return <ClinicalInformation />
      },
      renderSectionInfo: () => {
        return <div></div>
      },
    }))

    // Initialize Redux state with initial data if provided
    useEffect(() => {
      if (initialData && Object.keys(initialData).length > 0 && !formData.clinical) {
        const initialClinicalData = {
          clinicalNote: String(initialData?.clinicalNote || ''),
          optOutClinicianNotes: Boolean(initialData?.optOutClinicianNotes || false),
        }
        dispatch(updateClinicalData(initialClinicalData))
        onDataChange?.(initialClinicalData)
      }
    }, [initialData, formData.clinical, dispatch, onDataChange])

    // Initialize with preloaded data and mark as complete if populated
    useEffect(() => {
      const isComplete = clinicalData.clinicalNote.trim() !== '' || clinicalData.optOutClinicianNotes
      onComplete?.(isComplete)
    }, [clinicalData, onComplete])

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = e.target
      const updateData = { [name]: value }

      // Update Redux state
      dispatch(updateClinicalData(updateData))

      // Also call the callback for backward compatibility
      const newData = { ...clinicalData, ...updateData }
      onDataChange?.(newData)

      // Check completion status after data change
      const newIsComplete = newData.clinicalNote.trim() !== '' || newData.optOutClinicianNotes
      onComplete?.(newIsComplete)
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target
      const newData = { ...clinicalData, [name]: checked }
      newData.clinicalNote = newData.optOutClinicianNotes ? '' : newData.clinicalNote
      dispatch(updateClinicalData(newData))
      onDataChange?.(newData)

      // Check completion status after data change
      const newIsComplete = newData.clinicalNote.trim() !== '' || newData.optOutClinicianNotes
      onComplete?.(newIsComplete)
    }

    // Function to highlight references in text
    const highlightReferences = (text: string) => {
      // Only highlight on click, not on hover
      const refToHighlight = clickedReference
      if (!refToHighlight) {
        return text.replace(/</g, '&lt;').replace(/>/g, '&gt;')
      }

      // Escape HTML characters first
      const escapedText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;')

      // Highlight the specific reference
      const refPattern = new RegExp(`\\[${refToHighlight}\\]`, 'g')
      return escapedText.replace(
        refPattern,
        `<span style="background-color: #93C5FD; color: #1E3A8A; padding: 2px 4px; border-radius: 3px; font-weight: 600;">[${refToHighlight}]</span>`
      )
    }

    // Function to scroll to reference
    const scrollToReference = (refNumber: number) => {
      const currentElement = activeReference ? highlightedRef.current : textareaRef.current
      if (!currentElement) return

      const text = clinicalData.optOutClinicianNotes ? '' : clinicalData.clinicalNote
      const refPattern = `[${refNumber}]`
      const index = text.indexOf(refPattern)

      if (index !== -1) {
        // Calculate the line number of the reference
        const textBeforeRef = text.substring(0, index)
        const lineHeight = 20 // Approximate line height in pixels for text-sm
        const linesBeforeRef = textBeforeRef.split('\n').length - 1

        // Scroll to put the reference at the top of the visible area
        currentElement.scrollTop = Math.max(0, linesBeforeRef * lineHeight)
      }
    }

    return (
      <div className="flex h-full flex-col space-y-4">
        {/* Clinical Notes Section - 5x height with collapsible header */}
        <div className="min-h-0 flex-[5] rounded-lg border border-gray-300">
          <button
            type="button"
            onClick={() => setIsClinicalNotesExpanded(!isClinicalNotesExpanded)}
            className="w-full rounded-t-lg border-b border-gray-300 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-3 text-gray-500">{isClinicalNotesExpanded ? '−' : '+'}</span>
                <h4 className="form-label">
                  Clinical Note <span className="text-red-500">*</span>
                </h4>
              </div>
            </div>
          </button>

          {isClinicalNotesExpanded && (
            <div className="p-4">
              {activeReference ? (
                // Show highlighted version when reference is clicked (read-only mode)
                <div
                  ref={highlightedRef}
                  className="w-full overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  style={{
                    fontFamily: 'inherit',
                    lineHeight: '20px',
                    height: '20px',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: highlightReferences(clinicalData.optOutClinicianNotes ? '' : clinicalData.clinicalNote),
                  }}
                  onClick={() => setClickedReference(null)}
                />
              ) : (
                // Show editable textarea when no reference is clicked
                <textarea
                  ref={textareaRef}
                  id="clinicalNote"
                  name="clinicalNote"
                  value={clinicalData.optOutClinicianNotes ? '' : clinicalData.clinicalNote}
                  onChange={handleInputChange}
                  onClick={() => setClickedReference(null)}
                  style={{
                    resize: 'none',
                    height: '250px',
                  }}
                  required
                  disabled={clinicalData.optOutClinicianNotes}
                  className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm selection:bg-blue-300 selection:text-blue-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    clinicalData.optOutClinicianNotes
                      ? 'cursor-not-allowed bg-gray-100 text-gray-500'
                      : 'focus:border-indigo-500'
                  }`}
                  placeholder="Enter your notes here."
                />
              )}
            </div>
          )}
        </div>

        {/* Collapsible Table Section */}
        <div className="min-h-0 flex-1 rounded-lg border border-gray-300">
          <button
            type="button"
            onClick={() => setIsTableExpanded(!isTableExpanded)}
            className="w-full rounded-t-lg border-b border-gray-300 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <div className="flex items-center">
              <span className="mr-3 text-gray-500">{isTableExpanded ? '−' : '+'}</span>
              <h4 className="form-label">References</h4>
            </div>
          </button>

          {isTableExpanded && (
            <div className="h-80 overflow-y-auto">
              <table className="table-text w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="table-header w-8 px-3 py-2 text-left">#</th>
                    <th className="table-header px-3 py-2 text-left">FHIR Resource</th>
                    <th className="table-header px-3 py-2 text-left">HL7 v2 Segment</th>
                    <th className="table-header px-3 py-2 text-left">C-CDA / CCD Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {referenceData.map((ref) => (
                    <tr
                      key={ref.id}
                      className={`cursor-pointer transition-colors hover:bg-blue-50 ${
                        clickedReference === ref.id ? 'bg-blue-200' : hoveredReference === ref.id ? 'bg-blue-100' : ''
                      }`}
                      onClick={() => {
                        if (clickedReference === ref.id) {
                          // If clicking the same row, toggle off
                          setClickedReference(null)
                        } else {
                          // Set new clicked reference
                          setClickedReference(ref.id)
                          scrollToReference(ref.id)
                        }
                      }}
                      onMouseEnter={() => {
                        if (!clickedReference) {
                          setHoveredReference(ref.id)
                        }
                      }}
                      onMouseLeave={() => {
                        if (!clickedReference) {
                          setHoveredReference(null)
                        }
                      }}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{ref.id}</td>
                      <td className="px-3 py-2 text-gray-700">{ref.fhir}</td>
                      <td className="px-3 py-2 text-gray-700">{ref.hl7}</td>
                      <td className="px-3 py-2 text-gray-700">{ref.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Opt-out Checkbox - Moved to bottom */}
        <div
          className="flex cursor-pointer items-center rounded-md border border-gray-300 p-3 transition-colors hover:bg-gray-50"
          onClick={() => {
            const newValue = !clinicalData.optOutClinicianNotes
            const newData = { ...clinicalData, optOutClinicianNotes: newValue }
            newData.clinicalNote = newValue ? '' : newData.clinicalNote
            dispatch(updateClinicalData(newData))
            onDataChange?.(newData)

            // Check completion status after data change
            const newIsComplete = newData.clinicalNote.trim() !== '' || newData.optOutClinicianNotes
            onComplete?.(newIsComplete)
          }}
        >
          <input
            id="optOutClinicianNotes"
            name="optOutClinicianNotes"
            type="checkbox"
            checked={clinicalData.optOutClinicianNotes}
            onChange={handleCheckboxChange}
            className="h-4 w-4 rounded border-2 border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="optOutClinicianNotes" className="body-text ml-2 block cursor-pointer">
            Opt out of entering clinician notes
          </label>
        </div>
      </div>
    )
  }
)

export default ClinicalInformation
